import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { useNavigate } from 'react-router-dom';
import { Survey } from 'survey-react-ui';
import 'survey-core/defaultV2.min.css';
import '../styles/smart-form.less';
import { SmartFormChat } from '../components/SmartFormChat';
import { TextareaMicOverlay } from '../components/TextareaMicOverlay';
import { extractAttachments, stripAttachmentContent, dataUriToBlob, } from '../lib/attachments';
import { DOCUMENT_PARSE_ACCEPT, DOCUMENT_PARSE_FILE_HINT, DOCUMENT_PARSE_UNSUPPORTED_MESSAGE, detectDocumentParseKind, isAllowedDocumentParseFile, } from '../lib/documentParseFile';
import { isUploadTooLarge, UPLOAD_TOO_LARGE_MESSAGE } from '../lib/uploadLimits';
import { applySurveyData } from '../lib/smartFill/surveyData';
import { runDocumentParse, runSmartFill, SmartFillError, } from '../services/smartFill';
import { getOrLoadFormSession, getValidFormSession, invalidateFormSession, } from '../services/formSession';
import { ensureSignInUserInfo } from '../services/signinUserInfo';
import { SiteUserPicker } from '../components/SiteUserPicker';
import { sortLookupUsers } from '../lib/siteUsers';
import { defaultLookupValue, ensureIncidentLookup, matchLookupValue, } from '../services/incidentLookup';
import { extractIncidentId, fetchPortalForms, submitFormReferral, uploadReferralAttachment, } from '../services/workflow';
import { uploadBlobToS3 } from '../services/s3Upload';
import { createReferralAttachedForm, fetchReferralDetail, snapshotReferralFormPdf, updateReferralAttachedForm, updateReferralSite, } from '../services/referrals';
import { OPEN_STATUS } from '../lib/referralTimeLimits';
import { isClosedIncidentStatus, l1FamilyIssueTypes } from '../lib/incidentLevels';
import { isReferralLinkChecked, setReferralLinkChecked, } from '../lib/supportingFormLinks';
import { bindSurvey, executePostSubmit, executeValidateThenSubmit, resetFafBindings, } from '../faf/fafHost';
const FORM_INSTANCE_SEPARATOR = '::instance::';
function catalogFormId(instanceId) {
    return instanceId.split(FORM_INSTANCE_SEPARATOR, 1)[0];
}
// crypto.randomUUID is unavailable outside secure contexts (plain-http hosts),
// where it would throw mid-render instead of just opening a tab.
function createSupportingInstanceId(formId) {
    const unique = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    return `${formId}${FORM_INSTANCE_SEPARATOR}${unique}`;
}
function contactKey(firstName, lastName, email) {
    return `${firstName}|${lastName}|${email}`.trim().toLowerCase();
}
function toPersonInvolved(contact, type, kind) {
    return {
        id: 0,
        incidentId: 0,
        type,
        kind,
        canModify: false,
        firstName: contact.firstName.trim(),
        lastName: contact.lastName.trim(),
        email: contact.email.trim() || null,
        gender: null,
        additionalFields: typeof contact.sourceUserId === 'number' && contact.sourceUserId > 0
            ? { userId: contact.sourceUserId }
            : null,
    };
}
async function bindFafForForm(instanceId, formId, survey, incidentId) {
    await bindSurvey({
        formId,
        bindingKey: instanceId,
        survey,
        host: {
            formId: Number(formId) || formId,
            incidentId: incidentId ?? null,
            pageUrl: typeof window !== 'undefined' ? window.location.href : null,
        },
    });
}
/**
 * Stable stringify so key order alone never reads as an edit. Used to skip
 * re-saving (and re-attaching a PDF for) forms the user never touched — the
 * external API has no attachment delete, so every extra snapshot is permanent.
 */
function formDataFingerprint(data) {
    const normalize = (value) => {
        if (Array.isArray(value))
            return value.map(normalize);
        if (value !== null && typeof value === 'object') {
            return Object.fromEntries(Object.entries(value)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([key, child]) => [key, normalize(child)]));
        }
        return value;
    };
    return JSON.stringify(normalize(stripAttachmentContent(data)));
}
async function snapshotSavedFormsPdf(incidentId, forms) {
    let ok = 0;
    let failed = 0;
    for (const form of forms) {
        try {
            await snapshotReferralFormPdf({
                incidentId,
                fileName: form.fileName,
                formId: form.formId ?? null,
                surveyJson: form.surveyJson ?? null,
                formData: form.formData,
            });
            ok += 1;
        }
        catch (err) {
            failed += 1;
            console.error('[form-pdf-snapshot] client failed', {
                incidentId,
                fileName: form.fileName,
                formId: form.formId ?? null,
                error: err instanceof Error ? err.message : String(err),
            });
        }
    }
    return { ok, failed };
}
function collectSurveyAttachments(preferredSurvey, preferredFormId, openFormIds, portalForms) {
    const attachments = [];
    const formIds = Array.from(new Set(openFormIds.filter((id) => Boolean(id))));
    for (const formId of formIds) {
        const form = portalForms.find((entry) => entry.formId === catalogFormId(formId));
        const model = preferredSurvey && formId === preferredFormId
            ? preferredSurvey
            : getValidFormSession(formId)?.survey;
        if (!model)
            continue;
        for (const attachment of extractAttachments(model.data)) {
            attachments.push({
                ...attachment,
                fieldPath: `${formId}:${attachment.fieldPath}`,
                sourceLabel: form?.name ?? 'Form upload',
            });
        }
    }
    return attachments;
}
function attachmentName(name) {
    return name.trim().toLowerCase();
}
async function uploadIncidentAttachments(caseId, surveyAttachments, sourceDocuments, alreadyAttached = []) {
    const candidates = [
        ...surveyAttachments.map((attachment) => ({
            name: attachment.name,
            type: attachment.type,
            blob: dataUriToBlob(attachment.content, attachment.type),
        })),
        ...sourceDocuments.map((document) => ({
            name: document.file.name,
            type: document.file.type,
            blob: document.file,
        })),
    ];
    // Kokomo exposes no attachment delete, so any re-upload is a permanent
    // duplicate. Identity is name+size, which catches the same document arriving
    // from both a survey file field and the pending source list, or a re-parse.
    const seenBySize = new Set();
    const seenByName = new Set();
    for (const attachment of alreadyAttached) {
        const name = attachmentName(attachment.name);
        if (typeof attachment.size === 'number' && attachment.size > 0) {
            seenBySize.add(`${name}:${attachment.size}`);
        }
        else {
            // No size to compare against; fall back to matching on name alone.
            seenByName.add(name);
        }
    }
    const inputs = candidates.filter(({ name, blob }) => {
        const shortName = attachmentName(name);
        const key = `${shortName}:${blob.size}`;
        if (seenByName.has(shortName) || seenBySize.has(key)) {
            console.log('[referral submit] skipped duplicate attachment', { name, size: blob.size });
            return false;
        }
        seenBySize.add(key);
        return true;
    });
    const skipped = candidates.length - inputs.length;
    const outcomes = await Promise.allSettled(inputs.map(async ({ name, type, blob }) => {
        const uploaded = await uploadBlobToS3(blob, {
            purpose: 'attachment',
            fileName: name,
            contentType: type || 'application/octet-stream',
            caseId,
        });
        const associated = await uploadReferralAttachment({
            caseId,
            fileName: name,
            key: uploaded.key,
            size: uploaded.size,
        });
        if (!associated.associated) {
            throw new Error(`Uploaded ${name}, but could not associate it with L1 ${caseId}.`);
        }
        return associated;
    }));
    const failed = outcomes.filter((outcome) => outcome.status === 'rejected').length;
    return { uploaded: outcomes.length - failed, failed, skipped };
}
async function runFafPostSubmit(referralSurvey, referralFormId, openIds) {
    try {
        if (referralSurvey && referralFormId) {
            await executePostSubmit(referralFormId, referralSurvey);
        }
        for (const formId of openIds) {
            if (formId === referralFormId)
                continue;
            const session = getValidFormSession(formId);
            if (session) {
                await executePostSubmit(formId, session.survey);
            }
        }
    }
    catch (err) {
        console.error('[FAF] formPostSubmit failed', err);
    }
}
export function NewReferralPage({ editReferralId } = {}) {
    const navigate = useNavigate();
    const isEditMode = typeof editReferralId === 'number' && editReferralId > 0;
    const surveyContainerRef = useRef(null);
    const [modeTab, setModeTab] = useState('form');
    const [file, setFile] = useState(null);
    const [uploadTarget, setUploadTarget] = useState('');
    const [pendingSourceDocuments, setPendingSourceDocuments] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [parseProgress, setParseProgress] = useState(null);
    const [preparingSubmit, setPreparingSubmit] = useState(false);
    const [error, setError] = useState('');
    /** Set only once the L1 itself is submitted — it also retires the review section. */
    const [success, setSuccess] = useState('');
    const [uploadNotice, setUploadNotice] = useState('');
    const [portalForms, setPortalForms] = useState([]);
    const [openFormIds, setOpenFormIds] = useState([]);
    const [activeFormId, setActiveFormId] = useState(null);
    const [formsCatalogError, setFormsCatalogError] = useState('');
    const [addFormMenuOpen, setAddFormMenuOpen] = useState(false);
    const addFormMenuRef = useRef(null);
    const [formLoading, setFormLoading] = useState(true);
    const [formError, setFormError] = useState('');
    const [formTitle, setFormTitle] = useState('');
    const [survey, setSurvey] = useState(null);
    const [formDefinition, setFormDefinition] = useState(null);
    const [loadedFormId, setLoadedFormId] = useState(null);
    const [pendingFillResult, setPendingFillResult] = useState(null);
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [pendingSupportingForms, setPendingSupportingForms] = useState([]);
    const [readyToSubmit, setReadyToSubmit] = useState(false);
    const [foundContacts, setFoundContacts] = useState([]);
    const [personTypes, setPersonTypes] = useState([]);
    const [personKinds, setPersonKinds] = useState([]);
    const [sites, setSites] = useState([]);
    const [siteUsers, setSiteUsers] = useState([]);
    const [siteId, setSiteId] = useState(null);
    const [l1IssueTypes, setL1IssueTypes] = useState([]);
    const [issueTypeId, setIssueTypeId] = useState(null);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [editBootstrapping, setEditBootstrapping] = useState(isEditMode);
    const [editIncidentLevel, setEditIncidentLevel] = useState(null);
    const [hasAttachedL1Form, setHasAttachedL1Form] = useState(false);
    const editSeedsRef = useRef({});
    const appliedEditSeedsRef = useRef(new Set());
    const reviewSectionRef = useRef(null);
    const siteFieldRef = useRef(null);
    const issueTypeFieldRef = useRef(null);
    /** Site the L1 already had when editing, so we only PUT when it actually changes. */
    const editSiteIdRef = useRef(null);
    const preparingSubmitRef = useRef(false);
    const formScrollPositions = useRef({});
    const pendingScrollRestoreId = useRef(null);
    const linkSyncingRef = useRef(false);
    const openFormIdsRef = useRef(openFormIds);
    const activeFormIdRef = useRef(activeFormId);
    const portalFormsRef = useRef(portalForms);
    const addSupportingFormRef = useRef(() => undefined);
    const removeSupportingFormRef = useRef(() => undefined);
    openFormIdsRef.current = openFormIds;
    activeFormIdRef.current = activeFormId;
    portalFormsRef.current = portalForms;
    // Drop cached SurveyJS sessions / smart-form chat so this visit starts blank.
    // Form definition cache is kept so the empty form still loads quickly.
    useEffect(() => {
        invalidateFormSession();
        resetFafBindings();
    }, []);
    const activeForm = portalForms.find((form) => activeFormId != null && form.formId === catalogFormId(activeFormId)) ?? null;
    const isReferralForm = activeForm?.kind === 'referral';
    const referralForm = portalForms.find((form) => form.kind === 'referral') ?? null;
    const openForms = openFormIds
        .map((instanceId) => {
        const form = portalForms.find((entry) => entry.formId === catalogFormId(instanceId));
        return form ? { ...form, instanceId } : null;
    })
        .filter((form) => Boolean(form));
    const availableSupportingForms = portalForms.filter((form) => form.kind === 'supporting');
    const includedContactCount = foundContacts.filter((contact) => contact.include).length;
    const isL15Edit = isEditMode && editIncidentLevel === 'L1.5';
    const canPrepareWithoutL1Form = isL15Edit && !hasAttachedL1Form;
    const incidentNoun = isL15Edit ? 'L1.5' : 'L1';
    useEffect(() => {
        if (uploadTarget === 'other')
            return;
        if (openFormIds.includes(uploadTarget))
            return;
        if (activeFormId && openFormIds.includes(activeFormId)) {
            setUploadTarget(activeFormId);
            return;
        }
        setUploadTarget(openFormIds[0] ?? 'other');
    }, [uploadTarget, openFormIds, activeFormId]);
    const getReferralSurvey = useCallback(() => {
        if (!referralForm)
            return null;
        const session = getValidFormSession(referralForm.formId);
        if (session)
            return session.survey;
        if (isReferralForm && survey)
            return survey;
        return null;
    }, [referralForm, isReferralForm, survey]);
    const syncReferralLink = useCallback((formId, checked) => {
        const form = portalFormsRef.current.find((entry) => entry.formId === formId);
        if (!form?.link)
            return;
        const referralSurvey = getReferralSurvey();
        if (!referralSurvey)
            return;
        linkSyncingRef.current = true;
        try {
            setReferralLinkChecked(referralSurvey, form.link, checked);
        }
        finally {
            // SurveyJS may fire onValueChanged synchronously.
            queueMicrotask(() => {
                linkSyncingRef.current = false;
            });
        }
    }, [getReferralSurvey]);
    const selectForm = useCallback((formId) => {
        if (formId === activeFormId)
            return;
        if (activeFormId) {
            formScrollPositions.current[activeFormId] = window.scrollY;
        }
        pendingScrollRestoreId.current = formId;
        setLoadedFormId(null);
        setFormLoading(true);
        setActiveFormId(formId);
        setUploadTarget(formId);
        setAddFormMenuOpen(false);
    }, [activeFormId]);
    const addSupportingForm = useCallback((formId, options = {}) => {
        const meta = portalFormsRef.current.find((entry) => entry.formId === formId);
        if (meta?.kind === 'referral')
            return;
        const activate = options.activate !== false;
        const instanceId = createSupportingInstanceId(formId);
        setOpenFormIds((prev) => {
            const next = [...prev, instanceId];
            openFormIdsRef.current = next;
            return next;
        });
        if (activate) {
            selectForm(instanceId);
        }
        else {
            setAddFormMenuOpen(false);
        }
        syncReferralLink(formId, true);
    }, [selectForm, syncReferralLink]);
    const removeSupportingForm = useCallback((formId) => {
        const form = portalForms.find((entry) => entry.formId === catalogFormId(formId));
        if (!form || form.kind !== 'supporting')
            return;
        delete formScrollPositions.current[formId];
        invalidateFormSession(formId);
        resetFafBindings(formId);
        setOpenFormIds((prev) => {
            const next = prev.filter((id) => id !== formId);
            openFormIdsRef.current = next;
            if (!next.some((id) => catalogFormId(id) === form.formId)) {
                syncReferralLink(form.formId, false);
            }
            if (activeFormId === formId) {
                const referral = portalForms.find((entry) => entry.kind === 'referral');
                const referralStillOpen = Boolean(referral && next.includes(referral.formId));
                const fallbackId = referralStillOpen
                    ? referral.formId
                    : next[0] ?? null;
                if (fallbackId) {
                    pendingScrollRestoreId.current = fallbackId;
                    setActiveFormId(fallbackId);
                }
                else {
                    setActiveFormId(null);
                }
            }
            return next;
        });
    }, [activeFormId, portalForms, syncReferralLink]);
    addSupportingFormRef.current = addSupportingForm;
    removeSupportingFormRef.current = removeSupportingForm;
    useEffect(() => {
        ensureIncidentLookup()
            .then((lookup) => {
            setPersonTypes(lookup.personTypes);
            setPersonKinds(lookup.personKinds);
            setSites(lookup.sites);
            setSiteUsers(sortLookupUsers(lookup.users));
            const { root, options } = l1FamilyIssueTypes(lookup.issueTypes);
            setL1IssueTypes(options);
            if (root)
                setIssueTypeId(root.id);
            // Only pre-select when there is nothing to choose between.
            if (lookup.sites.length === 1) {
                setSiteId(lookup.sites[0].id);
            }
        })
            .catch((err) => console.error('[incidentLookup] failed', err));
    }, []);
    // Grouped by parent so schools sit under their district in the picker.
    const siteGroups = useMemo(() => {
        const groups = new Map();
        const sorted = [...sites].sort((a, b) => `${a.parentName ?? ''} ${a.name ?? ''}`.localeCompare(`${b.parentName ?? ''} ${b.name ?? ''}`));
        for (const site of sorted) {
            const key = site.parentName?.trim() || 'Top level';
            const existing = groups.get(key);
            if (existing) {
                existing.push(site);
            }
            else {
                groups.set(key, [site]);
            }
        }
        return [...groups.entries()];
    }, [sites]);
    // Both seeded from the role note the AI returned, until the user overrides them.
    const resolveContactType = useCallback((contact) => contact.type ??
        matchLookupValue(contact.note, personTypes) ??
        defaultLookupValue(personTypes), [personTypes]);
    const resolveContactKind = useCallback((contact) => contact.kind ??
        matchLookupValue(contact.note, personKinds) ??
        defaultLookupValue(personKinds), [personKinds]);
    const handleContactsFound = useCallback((contacts) => {
        setFoundContacts((prev) => {
            const seen = new Set(prev.map((c) => contactKey(c.firstName, c.lastName, c.email)));
            const additions = [];
            for (const contact of contacts) {
                const email = contact.email ?? '';
                const key = contactKey(contact.firstName, contact.lastName, email);
                if (seen.has(key))
                    continue;
                seen.add(key);
                additions.push({
                    key: `contact-${Date.now()}-${additions.length}-${Math.random().toString(36).slice(2)}`,
                    firstName: contact.firstName,
                    lastName: contact.lastName,
                    email,
                    note: contact.note,
                    type: null,
                    kind: null,
                    include: true,
                    sourceUserId: null,
                });
            }
            return additions.length ? [...prev, ...additions] : prev;
        });
    }, []);
    const updateContactText = useCallback((key, field, value) => {
        setFoundContacts((prev) => prev.map((c) => (c.key === key ? { ...c, [field]: value } : c)));
    }, []);
    const updateContactEnum = useCallback((key, field, value) => {
        setFoundContacts((prev) => prev.map((c) => (c.key === key ? { ...c, [field]: value } : c)));
    }, []);
    const toggleContact = useCallback((key, include) => {
        setFoundContacts((prev) => prev.map((c) => (c.key === key ? { ...c, include } : c)));
    }, []);
    const addContact = useCallback(() => {
        setFoundContacts((prev) => [
            ...prev,
            {
                key: `contact-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                firstName: '',
                lastName: '',
                email: '',
                note: '',
                type: null,
                kind: null,
                include: true,
                sourceUserId: null,
            },
        ]);
    }, []);
    const applySiteUserToContact = useCallback((key, user) => {
        setFoundContacts((prev) => prev.map((contact) => {
            if (contact.key !== key)
                return contact;
            if (!user)
                return { ...contact, sourceUserId: null };
            return {
                ...contact,
                sourceUserId: user.id,
                firstName: user.firstName?.trim() ?? '',
                lastName: user.lastName?.trim() ?? '',
                email: user.email?.trim() ?? '',
            };
        }));
    }, []);
    const removeContact = useCallback((key) => {
        setFoundContacts((prev) => prev.filter((c) => c.key !== key));
    }, []);
    // "Prepare for submit" validates via SurveyJS, then runs one smart-fill pass so
    // AI can extract people involved before the review / Submit L1 step.
    const handleSurveyCompleting = useCallback((sender, options) => {
        options.allowComplete = false;
        if (preparingSubmitRef.current || !formDefinition) {
            console.warn('[prepare-submit] ignored', {
                alreadyRunning: preparingSubmitRef.current,
                hasFormDefinition: Boolean(formDefinition),
            });
            return;
        }
        preparingSubmitRef.current = true;
        setError('');
        setUploadNotice('');
        setPreparingSubmit(true);
        setPendingAttachments(collectSurveyAttachments(sender, referralForm?.formId ?? activeFormId, openFormIdsRef.current, portalFormsRef.current));
        // Ensure linked supporting forms are included even when the linked answer
        // was populated before the value-change listener was bound.
        let linkedOpenIds = openFormIdsRef.current;
        for (const form of portalFormsRef.current) {
            if (form.kind !== 'supporting' || !form.link)
                continue;
            if (!isReferralLinkChecked(sender, form.link))
                continue;
            if (linkedOpenIds.some((id) => catalogFormId(id) === form.formId))
                continue;
            linkedOpenIds = [...linkedOpenIds, createSupportingInstanceId(form.formId)];
        }
        if (linkedOpenIds !== openFormIdsRef.current) {
            openFormIdsRef.current = linkedOpenIds;
            setOpenFormIds(linkedOpenIds);
        }
        // Snapshot open supporting forms now so the review card stays accurate even
        // if link-field smart-fill briefly mutates referral answers.
        setPendingSupportingForms(linkedOpenIds
            .map((formId) => portalFormsRef.current.find((entry) => entry.formId === catalogFormId(formId)))
            .filter((form) => form != null && form.kind === 'supporting'));
        const details = 'From the completed L1 form data, identify all people who should be involved in this L1 (students, school contacts, parents/guardians, and any other contacts). Return contactInformationFound for each person with firstName, lastName, email when available, and a short note describing their role. Also fill any missing form fields you can confidently determine.';
        void (async () => {
            let fafStopped = false;
            try {
                const fafCtx = await executeValidateThenSubmit(isReferralForm ? referralForm?.formId ?? activeFormId : activeFormId, sender);
                if (fafCtx?.shouldStop) {
                    fafStopped = true;
                    return;
                }
                if (isReferralForm && formDefinition) {
                    const result = await runSmartFill({
                        details,
                        formDefinition: JSON.parse(JSON.stringify(formDefinition)),
                        currentData: sender.data,
                        conversationHistory: [],
                    });
                    setPendingFillResult({
                        result,
                        userMessage: 'Prepare for submit — identify people involved',
                        formId: referralForm?.formId ?? activeFormId ?? '',
                    });
                }
            }
            catch (err) {
                console.error('[prepare-submit] smart fill failed', err);
                setError(err instanceof SmartFillError || err instanceof Error
                    ? `Could not auto-detect people involved: ${err.message}`
                    : 'Could not auto-detect people involved.');
            }
            finally {
                setPreparingSubmit(false);
                // Guards re-entry only: never hold it past this run, or a fill that
                // never reaches the chat would disable Prepare for submit for good.
                preparingSubmitRef.current = false;
                if (fafStopped)
                    return;
                setReadyToSubmit(true);
                setTimeout(() => {
                    reviewSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 50);
            }
        })();
    }, [formDefinition, referralForm, activeFormId, isReferralForm]);
    const handlePrepareWithoutForm = useCallback(() => {
        if (preparingSubmitRef.current)
            return;
        preparingSubmitRef.current = true;
        setError('');
        setUploadNotice('');
        setPendingAttachments(collectSurveyAttachments(survey, activeFormId, openFormIdsRef.current, portalFormsRef.current));
        setPendingSupportingForms(openFormIdsRef.current
            .map((formId) => portalFormsRef.current.find((entry) => entry.formId === catalogFormId(formId)))
            .filter((form) => form != null && form.kind === 'supporting'));
        preparingSubmitRef.current = false;
        setReadyToSubmit(true);
        setTimeout(() => {
            reviewSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
    }, [survey, activeFormId]);
    const handleFinalSubmit = useCallback(async () => {
        if (!canPrepareWithoutL1Form && (!survey || !isReferralForm))
            return;
        if (siteId == null) {
            setError(`Select the site this ${incidentNoun} belongs to before submitting.`);
            siteFieldRef.current?.focus();
            return;
        }
        if (!isEditMode && issueTypeId == null) {
            setError('Select an L1 incident type before submitting.');
            issueTypeFieldRef.current?.focus();
            return;
        }
        setError('');
        setSuccess('');
        setUploadNotice('');
        setSubmitting(true);
        const attachments = collectSurveyAttachments(survey, isReferralForm ? referralForm?.formId ?? activeFormId : activeFormId, openFormIds, portalForms);
        const cleanData = survey
            ? stripAttachmentContent(survey.data)
            : {};
        console.log('[referral submit] SurveyJS data (stripped)', cleanData);
        console.log('[referral submit] attachments', attachments.map((a) => a.name));
        try {
            if (isEditMode && editReferralId) {
                const referralSeed = referralForm
                    ? editSeedsRef.current[referralForm.formId]
                    : undefined;
                if (siteId !== editSiteIdRef.current) {
                    await updateReferralSite(editReferralId, siteId);
                    editSiteIdRef.current = siteId;
                }
                const pdfForms = [];
                if (referralSeed?.attachedId && referralForm) {
                    const referralSession = getValidFormSession(referralForm.formId);
                    const referralSurveyModel = referralSession?.survey ?? (isReferralForm ? survey : null);
                    const referralSurveyJson = referralSession?.detail.survey &&
                        typeof referralSession.detail.survey === 'object' &&
                        !Array.isArray(referralSession.detail.survey)
                        ? referralSession.detail.survey
                        : isReferralForm && formDefinition && typeof formDefinition === 'object'
                            ? formDefinition
                            : null;
                    const rawL1 = referralSurveyModel?.data &&
                        typeof referralSurveyModel.data === 'object' &&
                        !Array.isArray(referralSurveyModel.data)
                        ? referralSurveyModel.data
                        : {};
                    const l1Data = stripAttachmentContent(rawL1);
                    if (formDataFingerprint(l1Data) ===
                        formDataFingerprint(referralSeed.formData ?? {})) {
                        console.log('[referral submit] L1 form unchanged; skipped re-save and PDF');
                    }
                    else {
                        await updateReferralAttachedForm({
                            id: referralSeed.attachedId,
                            formData: l1Data,
                            formTitle: referralSeed.formTitle,
                            formId: referralSeed.formId,
                            incidentId: editReferralId,
                            survey: referralSurveyJson,
                        });
                        pdfForms.push({
                            fileName: referralSeed.formTitle || referralForm.name || 'L1',
                            formId: referralSeed.formId ?? referralForm.formId ?? null,
                            surveyJson: referralSurveyJson,
                            formData: l1Data,
                        });
                    }
                }
                else if (!isL15Edit) {
                    throw new Error('Could not find the attached L1 form to update.');
                }
                let supportingUpdated = 0;
                let supportingCreated = 0;
                for (const formId of openFormIds) {
                    const catalogId = catalogFormId(formId);
                    const meta = portalForms.find((entry) => entry.formId === catalogId);
                    if (!meta || meta.kind !== 'supporting')
                        continue;
                    const { detail, survey: supportingSurvey } = await getOrLoadFormSession(formId, {
                        formId: catalogId,
                    });
                    const surveyJson = detail.survey && typeof detail.survey === 'object' && !Array.isArray(detail.survey)
                        ? detail.survey
                        : null;
                    const rawData = supportingSurvey.data &&
                        typeof supportingSurvey.data === 'object' &&
                        !Array.isArray(supportingSurvey.data)
                        ? supportingSurvey.data
                        : {};
                    const formData = stripAttachmentContent(rawData);
                    const seed = editSeedsRef.current[formId];
                    const formIdNum = Number(catalogId);
                    if (seed?.attachedId) {
                        if (formDataFingerprint(formData) === formDataFingerprint(seed.formData ?? {})) {
                            console.log('[referral submit] supporting form unchanged; skipped', {
                                formId,
                                name: meta.name,
                            });
                            continue;
                        }
                        await updateReferralAttachedForm({
                            id: seed.attachedId,
                            formData,
                            formTitle: seed.formTitle || meta.name,
                            formId: seed.formId ?? (Number.isFinite(formIdNum) ? formIdNum : null),
                            incidentId: editReferralId,
                            survey: surveyJson,
                        });
                        supportingUpdated += 1;
                    }
                    else if (Number.isFinite(formIdNum)) {
                        await createReferralAttachedForm({
                            incidentId: editReferralId,
                            formId: formIdNum,
                            formTitle: meta.name,
                            survey: surveyJson,
                            formData,
                        });
                        supportingCreated += 1;
                    }
                    else {
                        continue;
                    }
                    pdfForms.push({
                        fileName: seed?.formTitle || meta.name,
                        formId: seed?.formId ?? (Number.isFinite(formIdNum) ? formIdNum : formId),
                        surveyJson,
                        formData,
                    });
                }
                let attachmentNote = '';
                if (attachments.length > 0 || pendingSourceDocuments.length > 0) {
                    const { uploaded, failed, skipped } = await uploadIncidentAttachments(editReferralId, attachments, pendingSourceDocuments, existingAttachments);
                    const total = uploaded + failed;
                    if (failed > 0) {
                        attachmentNote = ` Uploaded ${uploaded} of ${total} new attachment(s); ${failed} failed.`;
                    }
                    else if (uploaded > 0) {
                        attachmentNote = ` ${uploaded} new attachment(s) uploaded.`;
                    }
                    if (skipped > 0) {
                        attachmentNote += ` ${skipped} already attached, skipped.`;
                    }
                }
                if (pdfForms.length > 0) {
                    const pdfResult = await snapshotSavedFormsPdf(editReferralId, pdfForms);
                    if (pdfResult.ok > 0 || pdfResult.failed > 0) {
                        attachmentNote +=
                            pdfResult.failed > 0
                                ? ` Form PDF(s): ${pdfResult.ok} attached, ${pdfResult.failed} failed.`
                                : ` ${pdfResult.ok} form PDF(s) attached.`;
                    }
                }
                const supportingNote = supportingUpdated + supportingCreated > 0
                    ? ` ${supportingUpdated} supporting form(s) updated${supportingCreated > 0 ? `, ${supportingCreated} added` : ''}.`
                    : '';
                await runFafPostSubmit(isL15Edit && !hasAttachedL1Form ? null : survey, isL15Edit && !hasAttachedL1Form ? null : referralForm?.formId ?? activeFormId, openFormIds);
                setSuccess(`${incidentNoun} updated successfully.${supportingNote}${attachmentNote}`);
                setReadyToSubmit(false);
                setPendingAttachments([]);
                setPendingSourceDocuments([]);
                setPendingSupportingForms([]);
                setFoundContacts([]);
                invalidateFormSession();
                resetFafBindings();
                setTimeout(() => navigate(`/referrals/${editReferralId}`), 1200);
                return;
            }
            const incidentPersonInvolved = foundContacts
                .filter((c) => c.include && (c.firstName.trim() || c.lastName.trim()))
                .map((contact) => toPersonInvolved(contact, resolveContactType(contact), resolveContactKind(contact)));
            console.log('[referral submit] incidentPersonInvolved', incidentPersonInvolved);
            const additionalForms = [];
            for (const formId of openFormIds) {
                const catalogId = catalogFormId(formId);
                const meta = portalForms.find((entry) => entry.formId === catalogId);
                if (!meta || meta.kind !== 'supporting')
                    continue;
                const { detail, survey: supportingSurvey } = await getOrLoadFormSession(formId, {
                    formId: catalogId,
                });
                const surveyJson = detail.survey && typeof detail.survey === 'object' && !Array.isArray(detail.survey)
                    ? detail.survey
                    : null;
                const rawData = supportingSurvey.data &&
                    typeof supportingSurvey.data === 'object' &&
                    !Array.isArray(supportingSurvey.data)
                    ? supportingSurvey.data
                    : {};
                additionalForms.push({
                    formId: catalogId,
                    formTitle: meta.name,
                    survey: surveyJson,
                    formData: stripAttachmentContent(rawData),
                });
            }
            console.log('[referral submit] additionalForms', additionalForms.map((f) => ({ formId: f.formId, keys: Object.keys(f.formData).length })));
            const userInfo = await ensureSignInUserInfo();
            const submitter = {
                userId: userInfo.detail.id,
                firstName: userInfo.detail.firstName ?? '',
                lastName: userInfo.detail.lastName ?? '',
                email: userInfo.detail.email ?? '',
            };
            console.log('[referral submit] submitter', submitter);
            if (siteId == null) {
                throw new Error('Select the site this L1 belongs to before submitting.');
            }
            if (issueTypeId == null) {
                throw new Error('Select an L1 incident type before submitting.');
            }
            const incident = await submitFormReferral(cleanData, incidentPersonInvolved, submitter, additionalForms, { siteId, issueTypeId });
            console.log('[referral submit] success', incident);
            const caseId = extractIncidentId(incident);
            console.log('[referral submit] resolved caseId', caseId);
            let attachmentNote = '';
            if (attachments.length > 0 || pendingSourceDocuments.length > 0) {
                if (caseId == null) {
                    console.warn('[referral submit] could not resolve incident id; attachments not uploaded');
                    attachmentNote =
                        ' (but the L1 id could not be determined, so attachments were not uploaded)';
                }
                else {
                    const { uploaded, failed, skipped } = await uploadIncidentAttachments(caseId, attachments, pendingSourceDocuments);
                    const total = uploaded + failed;
                    console.log('[referral attachments] uploaded', uploaded, 'failed', failed, 'skipped', skipped);
                    attachmentNote =
                        failed > 0
                            ? ` Uploaded ${uploaded} of ${total} attachment(s); ${failed} failed.`
                            : ` ${uploaded} attachment(s) uploaded.`;
                }
            }
            if (caseId != null) {
                const pdfForms = [
                    {
                        fileName: referralForm?.name || 'L1',
                        formId: referralForm?.formId ?? null,
                        surveyJson: formDefinition && typeof formDefinition === 'object' ? formDefinition : null,
                        formData: cleanData,
                    },
                    ...additionalForms.map((form) => ({
                        fileName: form.formTitle,
                        formId: form.formId,
                        surveyJson: form.survey,
                        formData: form.formData,
                    })),
                ];
                const pdfResult = await snapshotSavedFormsPdf(caseId, pdfForms);
                if (pdfResult.ok > 0 || pdfResult.failed > 0) {
                    attachmentNote +=
                        pdfResult.failed > 0
                            ? ` Form PDF(s): ${pdfResult.ok} attached, ${pdfResult.failed} failed.`
                            : ` ${pdfResult.ok} form PDF(s) attached.`;
                }
            }
            const supportingNote = additionalForms.length > 0
                ? ` ${additionalForms.length} supporting form(s) attached.`
                : '';
            await runFafPostSubmit(survey, referralForm?.formId ?? activeFormId, openFormIds);
            setSuccess(`L1 submitted successfully.${supportingNote}${attachmentNote}`);
            setReadyToSubmit(false);
            setPendingAttachments([]);
            setPendingSourceDocuments([]);
            setPendingSupportingForms([]);
            setFoundContacts([]);
            invalidateFormSession();
            resetFafBindings();
            setTimeout(() => navigate('/'), 1800);
        }
        catch (err) {
            console.error('[referral submit] failed', err);
            setError(err instanceof Error
                ? err.message
                : isEditMode
                    ? `Failed to update ${incidentNoun}. Please try again.`
                    : 'Failed to submit L1. Please try again.');
        }
        finally {
            setSubmitting(false);
        }
    }, [
        survey,
        navigate,
        foundContacts,
        isReferralForm,
        openFormIds,
        portalForms,
        isEditMode,
        isL15Edit,
        canPrepareWithoutL1Form,
        hasAttachedL1Form,
        incidentNoun,
        editReferralId,
        referralForm,
        activeFormId,
        formDefinition,
        pendingSourceDocuments,
        resolveContactType,
        resolveContactKind,
        siteId,
        issueTypeId,
    ]);
    const loadFormCatalog = useCallback(async () => {
        setFormsCatalogError('');
        try {
            const forms = await fetchPortalForms();
            setPortalForms(forms);
            const referral = forms.find((form) => form.kind === 'referral');
            const referralId = referral?.formId ?? forms[0]?.formId ?? null;
            setUploadTarget((current) => {
                if (current === 'other')
                    return current;
                if (forms.some((form) => form.formId === catalogFormId(current))) {
                    return current;
                }
                return referralId ?? 'other';
            });
            // Edit mode: bootstrap effect owns open/active form ids after detail loads.
            if (isEditMode) {
                return;
            }
            setOpenFormIds((current) => {
                const stillValid = current.filter((id) => forms.some((form) => form.formId === catalogFormId(id)));
                if (stillValid.length > 0)
                    return stillValid;
                return referralId ? [referralId] : [];
            });
            setActiveFormId((current) => {
                if (current && forms.some((form) => form.formId === catalogFormId(current))) {
                    return current;
                }
                return referralId;
            });
        }
        catch (err) {
            setFormsCatalogError(err instanceof Error ? err.message : 'Failed to load available forms');
        }
    }, [isEditMode]);
    useEffect(() => {
        if (!addFormMenuOpen)
            return;
        const onPointerDown = (event) => {
            if (!addFormMenuRef.current?.contains(event.target)) {
                setAddFormMenuOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape')
                setAddFormMenuOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [addFormMenuOpen]);
    const loadForm = useCallback(async (formId, force = false) => {
        setFormError('');
        const catalogId = catalogFormId(formId);
        const applyEditSeed = (model) => {
            const seed = editSeedsRef.current[formId];
            if (!seed || appliedEditSeedsRef.current.has(formId))
                return;
            model.data = { ...(seed.formData ?? {}) };
            appliedEditSeedsRef.current.add(formId);
        };
        if (force) {
            appliedEditSeedsRef.current.delete(formId);
            invalidateFormSession(formId, true);
            resetFafBindings(formId);
        }
        const cached = !force ? getValidFormSession(formId) : null;
        if (cached) {
            const formMeta = portalForms.find((form) => form.formId === catalogId);
            const allowPrepare = formMeta?.kind === 'referral' || canPrepareWithoutL1Form;
            cached.survey.completeText = 'Prepare for submit';
            cached.survey.showCompleteButton = Boolean(allowPrepare);
            applyEditSeed(cached.survey);
            await bindFafForForm(formId, catalogId, cached.survey, isEditMode ? editReferralId : null);
            if (activeFormIdRef.current !== formId)
                return;
            setFormTitle(cached.detail.title);
            setFormDefinition(cached.detail.survey);
            setSurvey(cached.survey);
            setLoadedFormId(formId);
            setFormLoading(false);
            return;
        }
        setFormLoading(true);
        // Keep the previous survey mounted until the next form is ready so a failed
        // load (or quick tab switch) does not flash an empty form.
        try {
            const formMeta = portalForms.find((form) => form.formId === catalogId);
            const { detail, survey: model } = await getOrLoadFormSession(formId, {
                formId: catalogId,
                allowSubmit: formMeta?.kind === 'referral' || canPrepareWithoutL1Form,
            });
            applyEditSeed(model);
            await bindFafForForm(formId, catalogId, model, isEditMode ? editReferralId : null);
            if (activeFormIdRef.current !== formId)
                return;
            setFormTitle(detail.title);
            setFormDefinition(detail.survey);
            setSurvey(model);
            setLoadedFormId(formId);
        }
        catch (err) {
            setFormError(err instanceof Error ? err.message : 'Failed to load form from workflow engine');
        }
        finally {
            if (activeFormIdRef.current === formId) {
                setFormLoading(false);
            }
        }
    }, [portalForms, isEditMode, editReferralId, canPrepareWithoutL1Form]);
    useEffect(() => {
        loadFormCatalog();
    }, [loadFormCatalog]);
    // Edit mode: load referral detail, seed form answers, open matching supporting forms.
    useEffect(() => {
        if (!isEditMode || !editReferralId) {
            setEditBootstrapping(false);
            return;
        }
        if (portalForms.length === 0)
            return;
        let cancelled = false;
        setEditBootstrapping(true);
        void (async () => {
            try {
                const detail = await fetchReferralDetail(editReferralId);
                if (cancelled)
                    return;
                const isL15 = detail.level === 'L1.5';
                const isL1 = !detail.parent && (detail.level === 'L1' || detail.level == null);
                if (isL15) {
                    if (!detail.parent || isClosedIncidentStatus(detail.status)) {
                        setError(!detail.parent
                            ? 'This L1.5 is missing its parent L1 and cannot be edited here.'
                            : 'This L1.5 can only be edited while it is open.');
                        navigate(`/referrals/${editReferralId}`, { replace: true });
                        return;
                    }
                }
                else if (isL1) {
                    if (detail.status !== OPEN_STATUS) {
                        setError('This L1 can only be edited while status is New.');
                        navigate(`/referrals/${editReferralId}`, { replace: true });
                        return;
                    }
                }
                else {
                    setError('This incident cannot be edited from this page.');
                    navigate(`/referrals/${editReferralId}`, { replace: true });
                    return;
                }
                setEditIncidentLevel(isL15 ? 'L1.5' : 'L1');
                const referral = portalForms.find((form) => form.kind === 'referral');
                const seeds = {};
                const nextOpenIds = [];
                // L1 always opens the referral form. L1.5 only opens it when a matching
                // attached L1 form already exists (copied from the parent) — never create one.
                if (referral && !isL15) {
                    nextOpenIds.push(referral.formId);
                }
                for (const form of detail.issueForms ?? []) {
                    if (!form.canUpdate || form.id <= 0)
                        continue;
                    const catalogId = form.formId != null && Number.isFinite(form.formId)
                        ? String(form.formId)
                        : null;
                    if (!catalogId)
                        continue;
                    const catalog = portalForms.find((entry) => entry.formId === catalogId);
                    if (!catalog)
                        continue;
                    const instanceId = catalog.kind === 'referral'
                        ? catalog.formId
                        : createSupportingInstanceId(catalog.formId);
                    seeds[instanceId] = {
                        attachedId: form.id,
                        formId: form.formId ?? null,
                        formTitle: form.formTitle,
                        formData: form.formData ?? {},
                    };
                    if (catalog.kind === 'supporting') {
                        nextOpenIds.push(instanceId);
                    }
                    else if (catalog.kind === 'referral' &&
                        isL15 &&
                        !nextOpenIds.includes(catalog.formId)) {
                        nextOpenIds.unshift(catalog.formId);
                    }
                }
                // L1 only: attach the primary referral seed by kind if formId didn't match.
                // Never do this on an L1.5 — the first updatable form may be supporting.
                if (!isL15 && referral && !seeds[referral.formId]) {
                    const main = detail.issueForms?.find((form) => form.canUpdate && form.id > 0) ?? null;
                    if (main) {
                        seeds[referral.formId] = {
                            attachedId: main.id,
                            formId: main.formId ?? (Number(referral.formId) || null),
                            formTitle: main.formTitle,
                            formData: main.formData ?? {},
                        };
                    }
                }
                if (!isL15 && referral && !seeds[referral.formId]) {
                    throw new Error('No updatable attached L1 form was found on this L1.');
                }
                const attachedL1 = Boolean(referral && seeds[referral.formId]);
                setHasAttachedL1Form(attachedL1);
                editSeedsRef.current = seeds;
                appliedEditSeedsRef.current = new Set();
                setExistingAttachments(detail.attachments ?? []);
                editSiteIdRef.current = detail.siteId > 0 ? detail.siteId : null;
                setSiteId(editSiteIdRef.current);
                // Prefill every open form session so unopened supporting tabs still save answers.
                for (const formId of nextOpenIds) {
                    const catalogId = catalogFormId(formId);
                    const meta = portalForms.find((entry) => entry.formId === catalogId);
                    const { survey: model } = await getOrLoadFormSession(formId, {
                        formId: catalogId,
                        allowSubmit: meta?.kind === 'referral' || (isL15 && !attachedL1),
                    });
                    if (cancelled)
                        return;
                    const seed = seeds[formId];
                    if (seed) {
                        model.data = { ...(seed.formData ?? {}) };
                        appliedEditSeedsRef.current.add(formId);
                    }
                    await bindFafForForm(formId, catalogId, model, editReferralId);
                }
                openFormIdsRef.current = nextOpenIds;
                setOpenFormIds(nextOpenIds);
                setActiveFormId(attachedL1 && referral
                    ? referral.formId
                    : nextOpenIds[0] ?? null);
                if (nextOpenIds.length === 0) {
                    setFormLoading(false);
                    setSurvey(null);
                    setFormDefinition(null);
                    setLoadedFormId(null);
                }
            }
            catch (err) {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Failed to load the incident for editing.');
                }
            }
            finally {
                if (!cancelled)
                    setEditBootstrapping(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isEditMode, editReferralId, portalForms, navigate]);
    useEffect(() => {
        if (editBootstrapping)
            return;
        if (!activeFormId) {
            setFormLoading(false);
            return;
        }
        loadForm(activeFormId);
    }, [activeFormId, loadForm, editBootstrapping]);
    // Keep referral form linked to supporting-form tabs (check ↔ add).
    // Skip when editing an L1.5 that has no attached L1 form — loading the catalog
    // referral session here would look like creating a new L1.
    useEffect(() => {
        if (!referralForm)
            return;
        if (isEditMode && !hasAttachedL1Form)
            return;
        let cancelled = false;
        let referralSurvey = null;
        const onValueChanged = (_sender, options) => {
            if (linkSyncingRef.current || !referralSurvey)
                return;
            const changedName = options.name ?? '';
            const shortName = changedName.includes('.')
                ? changedName.slice(changedName.lastIndexOf('.') + 1)
                : changedName;
            for (const form of portalFormsRef.current) {
                if (form.kind !== 'supporting' || !form.link)
                    continue;
                if (form.link.fieldName !== shortName && form.link.fieldName !== changedName) {
                    continue;
                }
                const checked = isReferralLinkChecked(referralSurvey, form.link);
                const isOpen = openFormIdsRef.current.some((id) => catalogFormId(id) === form.formId);
                if (checked && !isOpen) {
                    addSupportingFormRef.current(form.formId, { activate: false });
                }
                // Do not auto-remove when the link is unchecked. Smart-fill / prepare-submit
                // often rewrites linked fields and would wipe open supporting tabs. Users
                // remove tabs explicitly via the × control (which also unchecks the link).
            }
        };
        void (async () => {
            try {
                const { survey: model } = await getOrLoadFormSession(referralForm.formId, {
                    allowSubmit: true,
                });
                if (cancelled)
                    return;
                referralSurvey = model;
                // If the referral already has linked docs checked, open those tabs.
                setOpenFormIds((prev) => {
                    let next = prev;
                    for (const form of portalFormsRef.current) {
                        if (form.kind !== 'supporting' || !form.link)
                            continue;
                        if (!isReferralLinkChecked(model, form.link))
                            continue;
                        if (next.some((id) => catalogFormId(id) === form.formId))
                            continue;
                        const instanceId = createSupportingInstanceId(form.formId);
                        next = next === prev ? [...prev, instanceId] : [...next, instanceId];
                    }
                    openFormIdsRef.current = next;
                    return next;
                });
                model.onValueChanged.add(onValueChanged);
            }
            catch (err) {
                console.error('[supporting-form-link] failed to bind referral survey', err);
            }
        })();
        return () => {
            cancelled = true;
            referralSurvey?.onValueChanged.remove(onValueChanged);
        };
    }, [referralForm, isEditMode, hasAttachedL1Form]);
    useEffect(() => {
        if (!survey)
            return;
        if (isReferralForm || canPrepareWithoutL1Form) {
            survey.completeText = 'Prepare for submit';
            survey.showCompleteButton = true;
            survey.onCompleting.add(handleSurveyCompleting);
            return () => {
                survey.onCompleting.remove(handleSurveyCompleting);
            };
        }
        // Supporting forms on an L1 (or L1.5 that already has an L1 form):
        // Prepare for submit stays on the L1 tab.
        survey.showCompleteButton = false;
    }, [survey, handleSurveyCompleting, isReferralForm, canPrepareWithoutL1Form]);
    // Keep the prepare-submit supporting-forms review list in sync with open tabs.
    useEffect(() => {
        if (!readyToSubmit)
            return;
        setPendingSupportingForms(openFormIds
            .map((formId) => portalForms.find((entry) => entry.formId === catalogFormId(formId)))
            .filter((form) => form != null && form.kind === 'supporting'));
    }, [readyToSubmit, openFormIds, portalForms]);
    // The chat only accepts a fill for the tab it was produced for. Once the user
    // is somewhere else that result can never be shown, so drop it rather than
    // leave it pending forever.
    useEffect(() => {
        if (!pendingFillResult)
            return;
        if (pendingFillResult.formId === activeFormId)
            return;
        setPendingFillResult(null);
    }, [pendingFillResult, activeFormId]);
    const formReady = !editBootstrapping &&
        !formLoading &&
        !formError &&
        survey &&
        formDefinition &&
        activeFormId &&
        loadedFormId === activeFormId;
    const showSubmitReview = readyToSubmit &&
        !success &&
        modeTab === 'form' &&
        (isReferralForm || canPrepareWithoutL1Form);
    const pendingAttachmentCount = pendingAttachments.length + pendingSourceDocuments.length;
    // Restore this form's scroll after the survey mounts (header stays sticky).
    useLayoutEffect(() => {
        if (!formReady || !activeFormId)
            return;
        if (pendingScrollRestoreId.current !== activeFormId)
            return;
        pendingScrollRestoreId.current = null;
        const y = formScrollPositions.current[activeFormId] ?? 0;
        window.scrollTo({ top: y, left: 0, behavior: 'auto' });
    }, [formReady, activeFormId, survey]);
    async function handleUpload(e) {
        e.preventDefault();
        if (!file) {
            setError('Choose a file to upload before submitting.');
            return;
        }
        if (isUploadTooLarge(file.size)) {
            setError(UPLOAD_TOO_LARGE_MESSAGE);
            return;
        }
        const attachOnly = uploadTarget === 'other';
        const targetInstanceId = attachOnly ? null : uploadTarget;
        const targetForm = attachOnly
            ? null
            : portalForms.find((form) => form.formId === catalogFormId(uploadTarget)) ?? null;
        if (!attachOnly && !targetForm) {
            setError('Add the supporting form first, then choose it here.');
            return;
        }
        if (!attachOnly) {
            try {
                detectDocumentParseKind(file.name);
            }
            catch (err) {
                setError(err instanceof Error
                    ? err.message
                    : DOCUMENT_PARSE_UNSUPPORTED_MESSAGE);
                return;
            }
        }
        setError('');
        setUploadNotice('');
        setSubmitting(true);
        setParseProgress(null);
        const sourceDocument = {
            id: `source-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            file,
            sourceLabel: attachOnly ? 'Other — attachment only' : `Source for ${targetForm?.name}`,
            parsedForFormId: targetInstanceId,
        };
        // Re-parsing the same document must not queue a second copy of the file.
        setPendingSourceDocuments((current) => [
            ...current.filter((pending) => pending.file.name !== file.name || pending.file.size !== file.size),
            sourceDocument,
        ]);
        setFile(null);
        if (attachOnly) {
            setUploadNotice(`${file.name} is ready to attach to the L1 without parsing.`);
            setSubmitting(false);
            return;
        }
        const targetFormId = targetInstanceId;
        const targetCatalogId = targetForm.formId;
        const userMessage = `Read the document: 
    ${file.name}, I will try to fill out the form 
    with the information found in the document.`;
        try {
            const targetSession = targetFormId === activeFormId &&
                loadedFormId === activeFormId &&
                survey &&
                formDefinition
                ? { survey, detail: { survey: formDefinition } }
                : await getOrLoadFormSession(targetFormId, {
                    formId: targetCatalogId,
                    allowSubmit: targetForm?.kind === 'referral',
                });
            const targetDefinition = targetSession.detail.survey &&
                typeof targetSession.detail.survey === 'object' &&
                !Array.isArray(targetSession.detail.survey)
                ? targetSession.detail.survey
                : null;
            if (!targetDefinition) {
                throw new Error(`Could not load ${targetForm?.name ?? 'the selected form'}.`);
            }
            const uploaded = await uploadBlobToS3(file, {
                purpose: 'document-parse',
                fileName: file.name,
                contentType: file.type,
            });
            const result = await runDocumentParse({
                formDefinition: JSON.parse(JSON.stringify(targetDefinition)),
                currentData: targetSession.survey.data,
                conversationHistory: [],
                fileName: file.name,
                key: uploaded.key,
            }, ({ progress, elapsedMs }) => {
                setParseProgress(typeof progress === 'number'
                    ? `${progress}%`
                    : `${Math.round(elapsedMs / 1000)}s`);
            });
            applySurveyData(targetSession.survey, result.surveyData, targetDefinition);
            setPendingFillResult({ result, userMessage, formId: targetFormId });
            // Return to the form the document was parsed for, in case tabs changed
            // while the parse was running.
            selectForm(targetFormId);
            setModeTab('form');
        }
        catch (err) {
            console.error('[document-parse] failed', err);
            setError(`${err instanceof SmartFillError
                ? err.message
                : err instanceof Error
                    ? err.message
                    : 'Failed to parse document. Please try again.'} The original file is still ready to attach to the L1.`);
        }
        finally {
            setSubmitting(false);
            setParseProgress(null);
        }
    }
    return (_jsxs("div", { className: "page page--new-referral", children: [_jsxs("div", { className: "new-referral-chrome", children: [_jsxs("div", { className: "page-header page-header--compact", children: [_jsx("h1", { children: isEditMode ? `Edit ${incidentNoun} ${editReferralId}` : 'New L1' }), _jsx("p", { className: "page-subtitle", children: formTitle
                                    ? formTitle
                                    : isEditMode
                                        ? canPrepareWithoutL1Form
                                            ? 'Add supporting forms or documents, then prepare to save. An L1 form appears here only if it was copied from the parent.'
                                            : `Update the ${incidentNoun} form, then prepare to save`
                                        : 'Complete the form or upload an existing L1 document' })] }), _jsxs("div", { className: "tab-bar", children: [_jsx("button", { type: "button", className: `tab ${modeTab === 'form' ? 'active' : ''}`, onClick: () => setModeTab('form'), children: "Fill out form" }), _jsx("button", { type: "button", className: `tab ${modeTab === 'upload' ? 'active' : ''}`, onClick: () => {
                                    if (activeFormId)
                                        setUploadTarget(activeFormId);
                                    setModeTab('upload');
                                }, children: "Upload document" })] }), portalForms.length > 0 && (_jsxs("div", { className: "form-switcher", role: "tablist", "aria-label": "Forms", children: [openForms.map((form) => (_jsxs("div", { className: `form-switcher-item${form.instanceId === activeFormId ? ' active' : ''}`, children: [_jsxs("button", { type: "button", role: "tab", "aria-selected": form.instanceId === activeFormId, className: "form-switcher-tab", onClick: () => selectForm(form.instanceId), children: [form.name, openForms.filter((entry) => entry.formId === form.formId).length > 1
                                                ? ` (${openForms
                                                    .filter((entry) => entry.formId === form.formId)
                                                    .findIndex((entry) => entry.instanceId === form.instanceId) + 1})`
                                                : ''] }), form.kind === 'supporting' && (_jsx("button", { type: "button", className: "form-switcher-remove", "aria-label": `Remove ${form.name}`, title: "Remove form", onClick: (event) => {
                                            event.stopPropagation();
                                            removeSupportingForm(form.instanceId);
                                        }, children: "\u00D7" }))] }, form.instanceId))), _jsxs("div", { className: "add-form-menu", ref: addFormMenuRef, children: [_jsx("button", { type: "button", className: `form-switcher-tab form-switcher-tab--add${addFormMenuOpen ? ' is-open' : ''}`, "aria-expanded": addFormMenuOpen, "aria-haspopup": "listbox", onClick: () => setAddFormMenuOpen((open) => !open), children: "+ Add Attachment" }), addFormMenuOpen && (_jsxs("ul", { className: "add-form-menu-list", role: "listbox", "aria-label": "Add attachment", children: [availableSupportingForms.map((form) => (_jsx("li", { role: "option", children: _jsx("button", { type: "button", className: "add-form-menu-option", onClick: () => addSupportingForm(form.formId), children: form.name }) }, form.formId))), _jsx("li", { role: "option", children: _jsx("button", { type: "button", className: "add-form-menu-option", onClick: () => {
                                                        setUploadTarget('other');
                                                        setModeTab('upload');
                                                        setAddFormMenuOpen(false);
                                                    }, children: "Other \u2014 attach only" }) })] }))] })] }))] }), error && _jsx("div", { className: "alert alert-error", children: error }), success && _jsx("div", { className: "alert alert-success", children: success }), uploadNotice && !success && (_jsx("div", { className: "alert alert-success", children: uploadNotice })), formsCatalogError && (_jsxs("div", { className: "form-load-error", children: [_jsx("p", { children: formsCatalogError }), _jsx("button", { type: "button", className: "btn btn-secondary", onClick: () => loadFormCatalog(), children: "Retry" })] })), formLoading && !editBootstrapping && modeTab === 'form' && (_jsx("div", { className: "table-empty", children: "Loading form\u2026" })), editBootstrapping && modeTab === 'form' && (_jsxs("div", { className: "table-empty", children: ["Loading ", incidentNoun, "\u2026"] })), canPrepareWithoutL1Form &&
                !editBootstrapping &&
                !formLoading &&
                !activeFormId &&
                modeTab === 'form' && (_jsxs("div", { className: "table-empty l15-edit-empty", children: [_jsx("p", { children: "This L1.5 has no L1 form unless you copy it from the parent. Add a supporting form to fill it with the assistant, or upload a document." }), !readyToSubmit && !success && (_jsx("button", { type: "button", className: "btn btn-primary", onClick: handlePrepareWithoutForm, children: "Prepare to save" }))] })), formError && modeTab === 'form' && (_jsxs("div", { className: "form-load-error", children: [_jsx("p", { children: formError }), _jsx("button", { type: "button", className: "btn btn-secondary", onClick: () => activeFormId && loadForm(activeFormId, true), children: "Retry" })] })), formReady && modeTab === 'form' && (_jsxs(_Fragment, { children: [_jsx(SmartFormChat, { persistenceKey: activeFormId ?? undefined, survey: survey, formDefinition: formDefinition, surveyContainerRef: surveyContainerRef, pendingFillResult: loadedFormId === activeFormId &&
                            pendingFillResult?.formId === activeFormId
                            ? pendingFillResult
                            : null, onPendingFillHandled: () => setPendingFillResult(null), onContactsFound: isReferralForm ? handleContactsFound : undefined }, activeFormId), _jsxs("section", { className: "survey-panel-side survey-panel-full", children: [(submitting || preparingSubmit) && (_jsx("div", { className: "overlay-msg overlay-msg--viewport", role: "status", "aria-live": "polite", children: _jsxs("div", { className: "overlay-msg-content", children: [_jsx("div", { className: "spinner", "aria-hidden": "true" }), _jsx("span", { children: preparingSubmit
                                                ? isReferralForm
                                                    ? 'Detecting people involved…'
                                                    : 'Preparing…'
                                                : isEditMode
                                                    ? 'Saving…'
                                                    : 'Submitting…' })] }) })), _jsxs("div", { ref: surveyContainerRef, children: [_jsx(Survey, { model: survey }), _jsx(TextareaMicOverlay, { containerRef: surveyContainerRef, survey: survey })] })] })] })), showSubmitReview && (_jsxs("div", { className: "submit-review", ref: reviewSectionRef, children: [_jsx("h2", { className: "submit-review-title", children: isEditMode ? 'Review & save' : 'Review & submit' }), _jsxs("div", { className: "review-card site-review", children: [_jsx("h3", { className: "review-card-title", children: isEditMode ? 'Site' : 'Site and incident type' }), _jsx("p", { className: "review-card-note", children: isEditMode
                                    ? `Confirm the school or district this ${incidentNoun} belongs to. Changing it reassigns the ${incidentNoun}.`
                                    : 'Choose the school and the L1 issue type. The general parent L1 type is selected by default.' }), _jsxs("label", { className: "site-select-field", htmlFor: "referral-site", children: [_jsxs("span", { children: ["Site", ' ', _jsx("span", { className: "site-required", "aria-hidden": "true", children: "*" })] }), _jsxs("select", { id: "referral-site", ref: siteFieldRef, required: true, "aria-required": "true", className: siteId == null ? 'is-missing' : undefined, value: siteId == null ? '' : String(siteId), disabled: submitting || sites.length === 0, onChange: (event) => {
                                            const next = Number(event.target.value);
                                            setSiteId(Number.isFinite(next) && next > 0 ? next : null);
                                            setError('');
                                        }, children: [_jsx("option", { value: "", children: sites.length === 0 ? 'No sites available' : 'Select a site…' }), siteGroups.map(([parentName, groupSites]) => (_jsx("optgroup", { label: parentName, children: groupSites.map((site) => (_jsx("option", { value: site.id, children: site.name?.trim() || `Site ${site.id}` }, site.id))) }, parentName)))] })] }), !isEditMode && (_jsxs("label", { className: "site-select-field", htmlFor: "referral-issue-type", children: [_jsxs("span", { children: ["L1 incident type", ' ', _jsx("span", { className: "site-required", "aria-hidden": "true", children: "*" })] }), _jsxs("select", { id: "referral-issue-type", ref: issueTypeFieldRef, required: true, "aria-required": "true", className: issueTypeId == null ? 'is-missing' : undefined, value: issueTypeId == null ? '' : String(issueTypeId), disabled: submitting || l1IssueTypes.length === 0, onChange: (event) => {
                                            const next = Number(event.target.value);
                                            setIssueTypeId(Number.isFinite(next) && next > 0 ? next : null);
                                            setError('');
                                        }, children: [_jsx("option", { value: "", children: l1IssueTypes.length === 0
                                                    ? 'No L1 issue types available'
                                                    : 'Select an L1 issue type…' }), l1IssueTypes.map((issueType) => {
                                                const isParent = issueType.parentId == null || issueType.parentId === 0;
                                                const name = issueType.name?.trim() || `Issue type ${issueType.id}`;
                                                return (_jsx("option", { value: issueType.id, children: isParent ? `${name} (default)` : name }, issueType.id));
                                            })] })] }))] }), isEditMode && existingAttachments.length > 0 && (_jsxs("div", { className: "review-card attachment-confirm", children: [_jsxs("h3", { className: "attachment-confirm-title", children: [existingAttachments.length, " existing attachment", existingAttachments.length === 1 ? '' : 's'] }), _jsx("ul", { className: "attachment-list", children: existingAttachments.map((attachment) => (_jsxs("li", { className: "attachment-item", children: [_jsx("span", { className: "attachment-check", "aria-hidden": "true", children: "\u2713" }), _jsx("span", { className: "attachment-name", children: attachment.name })] }, attachment.id))) }), _jsxs("p", { className: "attachment-note", children: ["Already on this ", incidentNoun, ". New files from the form will be added below."] })] })), !isEditMode && (_jsxs("div", { className: "review-card contacts-review", children: [_jsxs("h3", { className: "review-card-title", children: ["People involved", includedContactCount > 0 ? ` (${includedContactCount})` : ''] }), _jsx("p", { className: "review-card-note", children: foundContacts.length === 0
                                    ? 'No people were detected from the form. Add anyone who should be involved in this L1.'
                                    : 'Review and edit each person. Uncheck anyone you don\u2019t want added, or add more people.' }), foundContacts.length > 0 && (_jsx("ul", { className: "contact-list", children: foundContacts.map((contact) => (_jsxs("li", { className: `contact-item${contact.include ? '' : ' is-excluded'}`, children: [_jsxs("label", { className: "contact-include", children: [_jsx("input", { type: "checkbox", checked: contact.include, onChange: (e) => toggleContact(contact.key, e.target.checked) }), _jsx("span", { className: "visually-hidden", children: "Include this person" })] }), _jsxs("div", { className: "contact-fields", children: [_jsx(SiteUserPicker, { className: "contact-user-picker", users: siteUsers, selectedUserId: contact.sourceUserId ?? null, onSelect: (user) => applySiteUserToContact(contact.key, user), disabled: submitting }), _jsx("input", { className: "contact-input", placeholder: "First name", value: contact.firstName, onChange: (e) => updateContactText(contact.key, 'firstName', e.target.value) }), _jsx("input", { className: "contact-input", placeholder: "Last name", value: contact.lastName, onChange: (e) => updateContactText(contact.key, 'lastName', e.target.value) }), _jsx("input", { className: "contact-input", placeholder: "Email", value: contact.email, onChange: (e) => updateContactText(contact.key, 'email', e.target.value) }), _jsx("select", { className: "contact-input contact-type", "aria-label": "Person type", value: resolveContactType(contact), onChange: (e) => updateContactEnum(contact.key, 'type', Number(e.target.value)), children: personTypes.map((type) => (_jsx("option", { value: type.value, children: type.name }, type.value))) }), _jsx("select", { className: "contact-input contact-type", "aria-label": "Person kind", value: resolveContactKind(contact), onChange: (e) => updateContactEnum(contact.key, 'kind', Number(e.target.value)), children: personKinds.map((kind) => (_jsx("option", { value: kind.value, children: kind.name }, kind.value))) }), contact.note && (_jsx("span", { className: "contact-note", children: contact.note }))] }), _jsx("button", { type: "button", className: "contact-remove-btn", "aria-label": `Remove ${contact.firstName || contact.lastName || 'person'}`, onClick: () => removeContact(contact.key), children: "Remove" })] }, contact.key))) })), _jsx("div", { className: "contact-list-actions", children: _jsx("button", { type: "button", className: "btn-secondary", disabled: submitting, onClick: addContact, children: "Add person" }) })] })), pendingAttachmentCount > 0 && (_jsxs("div", { className: "review-card attachment-confirm", children: [_jsxs("h3", { className: "attachment-confirm-title", children: [pendingAttachmentCount, " file", pendingAttachmentCount === 1 ? '' : 's', " ready to attach"] }), _jsxs("ul", { className: "attachment-list", children: [pendingAttachments.map((attachment) => (_jsxs("li", { className: "attachment-item", children: [_jsx("span", { className: "attachment-check", "aria-hidden": "true", children: "\u2713" }), _jsxs("span", { className: "attachment-name", children: [attachment.name, _jsx("small", { children: attachment.sourceLabel })] })] }, attachment.fieldPath))), pendingSourceDocuments.map((document) => (_jsxs("li", { className: "attachment-item", children: [_jsx("span", { className: "attachment-check", "aria-hidden": "true", children: "\u2713" }), _jsxs("span", { className: "attachment-name", children: [document.file.name, _jsx("small", { children: document.sourceLabel })] })] }, document.id)))] }), _jsx("p", { className: "attachment-note", children: isEditMode
                                    ? `These files will be added as new attachments to this ${incidentNoun}.`
                                    : 'These files will be created as attachments to this L1.' })] })), pendingSupportingForms.length > 0 && (_jsxs("div", { className: "review-card attachment-confirm supporting-forms-confirm", children: [_jsxs("h3", { className: "attachment-confirm-title", children: [pendingSupportingForms.length, " supporting form", pendingSupportingForms.length === 1 ? '' : 's', " ready to", ' ', isEditMode ? 'save' : 'attach'] }), _jsx("ul", { className: "attachment-list", children: pendingSupportingForms.map((form, index) => (_jsxs("li", { className: "attachment-item", children: [_jsx("span", { className: "attachment-check", "aria-hidden": "true", children: "\u2713" }), _jsx("span", { className: "attachment-name", children: form.name })] }, `${form.formId}-${index}`))) }), _jsx("p", { className: "attachment-note", children: isEditMode
                                    ? `These supporting forms will be updated or added on this ${incidentNoun}.`
                                    : 'These forms will also be attached to this L1.' })] })), isEditMode &&
                        existingAttachments.length === 0 &&
                        pendingAttachmentCount === 0 &&
                        pendingSupportingForms.length === 0 && (_jsx("p", { className: "attachment-note", children: siteId == null
                            ? 'Choose a site above to continue.'
                            : `Your ${incidentNoun} updates are ready to save.` })), _jsx("div", { className: "attachment-actions", children: _jsx("button", { type: "button", className: "btn btn-primary", onClick: handleFinalSubmit, disabled: submitting ||
                                siteId == null ||
                                (!isEditMode && issueTypeId == null), children: submitting
                                ? isEditMode
                                    ? 'Saving…'
                                    : 'Submitting…'
                                : isEditMode
                                    ? 'Save changes'
                                    : 'Submit L1' }) })] })), modeTab === 'upload' && (_jsxs("form", { className: "upload-panel", onSubmit: handleUpload, children: [_jsxs("label", { className: "upload-target-field", htmlFor: "document-use", children: [_jsx("span", { children: "Use document for" }), _jsxs("select", { id: "document-use", value: uploadTarget, disabled: submitting, onChange: (event) => {
                                    setUploadTarget(event.target.value);
                                    setError('');
                                }, children: [openForms.map((form) => (_jsx("option", { value: form.instanceId, children: form.kind === 'referral'
                                            ? `L1 — ${form.name}`
                                            : `${form.name}${openForms.filter((entry) => entry.formId === form.formId).length > 1
                                                ? ` (${openForms
                                                    .filter((entry) => entry.formId === form.formId)
                                                    .findIndex((entry) => entry.instanceId === form.instanceId) + 1})`
                                                : ''}` }, form.instanceId))), _jsx("option", { value: "other", children: "Other \u2014 attach only, do not parse" })] })] }), _jsxs("div", { className: "upload-dropzone", children: [_jsx("input", { type: "file", id: "referral-file", accept: uploadTarget === 'other' ? undefined : DOCUMENT_PARSE_ACCEPT, onChange: (e) => {
                                    const next = e.target.files?.[0] ?? null;
                                    if (uploadTarget !== 'other' &&
                                        next &&
                                        !isAllowedDocumentParseFile(next.name)) {
                                        setFile(null);
                                        e.target.value = '';
                                        setError(DOCUMENT_PARSE_UNSUPPORTED_MESSAGE);
                                        return;
                                    }
                                    if (next && isUploadTooLarge(next.size)) {
                                        setFile(null);
                                        e.target.value = '';
                                        setError(UPLOAD_TOO_LARGE_MESSAGE);
                                        return;
                                    }
                                    setError('');
                                    setFile(next);
                                } }, `${uploadTarget}-${pendingSourceDocuments.length}`), _jsx("label", { htmlFor: "referral-file", children: file ? (_jsxs(_Fragment, { children: [_jsx("strong", { children: file.name }), _jsxs("span", { children: [(file.size / 1024).toFixed(1), " KB"] })] })) : (_jsxs(_Fragment, { children: [_jsx("strong", { children: "Choose a file" }), _jsx("span", { children: uploadTarget === 'other'
                                                ? 'Any file type up to the attachment size limit'
                                                : DOCUMENT_PARSE_FILE_HINT })] })) })] }), _jsx("p", { className: "upload-test-note", children: uploadTarget === 'other' ? (_jsx(_Fragment, { children: "The original file will be attached to the L1 as-is and will not be parsed." })) : (_jsxs(_Fragment, { children: ["The selected file will be parsed to fill", ' ', _jsx("strong", { children: portalForms.find((form) => form.formId === catalogFormId(uploadTarget))?.name ??
                                        'the selected form' }), ". The original file will also be attached to the L1 for auditability."] })) }), pendingSourceDocuments.length > 0 && (_jsxs("div", { className: "pending-source-documents", children: [_jsx("strong", { children: "Documents ready to attach" }), _jsx("ul", { className: "attachment-list", children: pendingSourceDocuments.map((document) => (_jsxs("li", { className: "attachment-item", children: [_jsxs("span", { className: "attachment-name", children: [document.file.name, _jsx("small", { children: document.sourceLabel })] }), _jsx("button", { type: "button", className: "btn btn-ghost pending-document-remove", disabled: submitting, onClick: () => setPendingSourceDocuments((current) => current.filter((item) => item.id !== document.id)), children: "Remove" })] }, document.id))) })] })), _jsxs("div", { className: "upload-actions", children: [_jsx("button", { type: "button", className: "btn btn-ghost", onClick: () => navigate('/'), children: "Cancel" }), _jsx("button", { type: "submit", className: "btn btn-primary", disabled: submitting || formLoading || Boolean(formError) || !file, children: submitting
                                    ? parseProgress === null
                                        ? uploadTarget === 'other'
                                            ? 'Adding…'
                                            : 'Uploading & parsing…'
                                        : `Parsing… ${parseProgress}`
                                    : uploadTarget === 'other'
                                        ? 'Add attachment'
                                        : 'Parse document' })] })] }))] }));
}
